import { distanceKm } from '../../shared/distance_km.js';
import type { HotelCoordinates, HotelMapView, HotelOfferView } from '../models.js';

/*
 * O ENQUADRAMENTO do mapa de hotéis: quais pontos a câmera cabe e onde ela nasce.
 * Conta pura, sem Mapbox nem DOM; saiu de `hotel_map.tsx` pelo teto de linhas dele.
 */

export type MapPoint = readonly [number, number];

/**
 * O PISO DO ZOOM, e ele é geometria e não opção: o `fitBounds` só recebe
 * `maxZoom`, então a única forma de nenhum estado possível abrir em vista de
 * planeta é limitar o TAMANHO da caixa. Ponto mais longe que isto da âncora não
 * é conjunto com ela e fica de fora.
 *
 * O defeito que fixa (issue #2173): uma busca em `Porto` voltou com hotéis de
 * São Paulo, e a caixa obrigada a caber os dois — 9.000 km, 38° de arco — levou
 * o `fitBounds` ao zoom 1–2 num card de ~450px, o "Google Earth" do report. A
 * caixa passa a caber em 2× este raio (≤ ~4,5° de arco), e é isso que dá piso ao
 * zoom: no pior caso deste card (folga máxima, oferta na diagonal exata do teto)
 * o enquadramento para na casa do zoom 5 — nível de região, não de planeta.
 *
 * 250 km, e não os 100 km da guarda de região da borda: o mapa é o último
 * anteparo e não pode descartar oferta que a borda deixou passar de propósito —
 * só o absurdo que ela não viu (bloco antigo do histórico, host sem a guarda).
 */
const MAP_FRAME_MAX_ANCHOR_DISTANCE_KM = 250;

/** As ofertas que cabem no MESMO enquadramento da âncora. Sem âncora usável não
 *  há de onde medir e todas entram — o enquadramento é o das ofertas, e nele
 *  nenhuma é outlier. */
function frameableOffers<Offer extends { coordinates: HotelCoordinates }>(
  map: HotelMapView,
  offers: readonly Offer[],
): readonly Offer[] {
  const anchor = map.center;
  if (!anchor || !Number.isFinite(anchor.latitude) || !Number.isFinite(anchor.longitude)) {
    return offers;
  }
  return offers.filter(
    ({ coordinates }) => distanceKm(anchor, coordinates) <= MAP_FRAME_MAX_ANCHOR_DISTANCE_KM,
  );
}

export function framePoints(
  map: HotelMapView,
  offers: readonly (HotelOfferView & {
    coordinates: NonNullable<HotelOfferView['coordinates']>;
  })[],
): MapPoint[] {
  const points: MapPoint[] = frameableOffers(map, offers).map(({ coordinates }) => [
    coordinates.longitude,
    coordinates.latitude,
  ]);
  // A âncora só entra quando não há CAIXA de hotéis a enquadrar (nenhum pino,
  // ou um só). Com dois ou mais, o enquadramento é o dos hotéis (#3217 C5): o
  // pino da cidade cai longe do aglomerado e puxava a câmera — e o
  // "Reenquadrar" — para fora deles.
  if (points.length < 2 && map.center) points.push([map.center.longitude, map.center.latitude]);
  return points;
}

export function initialMapCoordinates(
  map: HotelMapView,
  offers: readonly (HotelOfferView & {
    coordinates: NonNullable<HotelOfferView['coordinates']>;
  })[],
): NonNullable<HotelMapView['center']> | undefined {
  // O ponto médio só vale entre pontos do mesmo conjunto: com a oferta fora do
  // enquadramento, a média cairia no meio do caminho entre as duas regiões — no
  // report, no meio do Atlântico — e seria essa a câmera do primeiro quadro.
  const framed = frameableOffers(map, offers);
  const offer = framed.length === 1 ? framed[0]?.coordinates : undefined;
  if (!map.center || !offer) return map.center ?? offer;
  return {
    latitude: (map.center.latitude + offer.latitude) / 2,
    longitude: (map.center.longitude + offer.longitude) / 2,
  };
}
